// Single source of truth for the drawer; BOGOS also writes to the cart, so every mutation re-reads /cart.js.
(function () {
  'use strict';

  if (window.cartState) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const path = (suffix) => `${root.replace(/\/+$/, '')}/${suffix}`;

  const ENDPOINTS = {
    get: path('cart.js'),
    add: path('cart/add.js'),
    change: path('cart/change.js'),
    update: path('cart/update.js'),
  };

  const JSON_HEADERS = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  // Written by sections/cart-drawer-custom.liquid; money values are integer pence.
  const defaults = {
    // 0 hides the bar; deliberately not a copy of the configured threshold.
    freeDeliveryThreshold: 0,
    subMark: 0,
    starterPackProducts: '',
    largestPackShipsFree: false,
    giftThreshold: null, // falls back to the active shipping threshold
    giftVariantIds: [],
  };

  const config = () => Object.assign({}, defaults, window.gpCartConfig || {});

  // Mirrors BOGOS checkCartItemIsGift.
  function isGiftLine(item) {
    if (!item) return false;

    const props = item.properties || {};

    if (props._gp_free_gift) return true;

    if (props._bogos_trigger_type && props._bogos_trigger) return true;

    const handle = item.handle || (item.url || '').split('/products/')[1] || '';
    if (handle.indexOf('-sca_clone_freegift') !== -1) return true;

    if (Array.isArray(item.tags) && item.tags.indexOf('bogos-gift') !== -1) return true;

    const giftIds = []
      .concat(config().giftVariantIds || [])
      .concat((window.FGSECOMAPP && window.FGSECOMAPP.variables && window.FGSECOMAPP.variables.giftIds) || [])
      .concat((window.FGSECOMAPP && window.FGSECOMAPP.variables && window.FGSECOMAPP.variables.giftIdsFunction) || [])
      .map(Number);

    return giftIds.indexOf(Number(item.variant_id || item.id)) !== -1;
  }

  class CartState extends EventTarget {
    constructor() {
      super();

      this.cart = null;
      this.freeGiftEligible = false;
      this.shippingProgress = {
        current: 0,
        threshold: 0,
        remaining: 0,
        met: false,
        percent: 0,
        reason: 'threshold',
      };

      // Comma-wrapped largest-pack variant ids accumulated across renders for the page's life.
      this._largestPacks = '';

      // Monotonic token so a slow response can never overwrite a newer one.
      this._token = 0;
      this._inFlight = null;

      this._listenForForeignWrites();
    }

    refresh() {
      if (this._inFlight) return this._inFlight;

      const token = ++this._token;

      this._inFlight = fetch(`${ENDPOINTS.get}?t=${Date.now()}`, {
        method: 'GET',
        headers: JSON_HEADERS,
        credentials: 'same-origin',
      })
        .then((response) => response.json())
        .then((cart) => {
          if (token !== this._token) return this.cart;
          this.setCart(cart);
          return cart;
        })
        .catch((error) => {
          console.warn('[gp-cart] refresh failed', error);
          return this.cart;
        })
        .finally(() => {
          this._inFlight = null;
        });

      return this._inFlight;
    }

    // FormData is posted untouched with no Content-Type so the browser sets the multipart boundary.
    add(items) {
      const isFormData = typeof FormData !== 'undefined' && items instanceof FormData;

      const init = isFormData
        ? {
            method: 'POST',
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            body: items,
          }
        : {
            method: 'POST',
            headers: JSON_HEADERS,
            credentials: 'same-origin',
            body: JSON.stringify(Array.isArray(items) ? { items } : items),
          };

      return fetch(ENDPOINTS.add, init).then((response) => this._settle(response));
    }

    // Used for cart attributes (free-gift dismissal), not line updates.
    update(payload) {
      return fetch(ENDPOINTS.update, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      }).then((response) => this._settle(response));
    }

    change(payload) {
      return fetch(ENDPOINTS.change, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'same-origin',
        body: JSON.stringify(payload),
      }).then((response) => this._settle(response));
    }

    // Check response.ok, not body.status: Shopify's 400 body has status "bad_request".
    _settle(response) {
      return response
        .json()
        .catch(() => ({}))
        .then((result) => {
          if (response.ok) return this.refresh();

          return this.refresh().then(() => {
            const message =
              (result && (result.description || result.message)) ||
              `Cart request failed (${response.status})`;

            throw Object.assign(new Error(message), {
              cartError: result || {},
              status: response.status,
            });
          });
        });
    }

    // The only place cart:updated is dispatched.
    setCart(cart) {
      this.cart = cart;
      this.recompute();

      this.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: {
            cart: this.cart,
            freeGiftEligible: this.freeGiftEligible,
            shippingProgress: this.shippingProgress,
          },
        })
      );
    }

    // Rules live in cart-free-delivery.js / gp-free-delivery-qualify.liquid; this only publishes the result.
    recompute() {
      const cart = this.cart;
      const settings = config();

      if (!cart) {
        this.freeGiftEligible = false;
        this.shippingProgress = {
          current: 0,
          threshold: 0,
          remaining: 0,
          met: false,
          percent: 0,
          reason: 'threshold',
        };
        return;
      }

      const items = cart.items || [];

      this._learnLargestPacks();

      const result = window.gpFreeDelivery
        ? window.gpFreeDelivery.qualify(cart, {
            threshold: settings.freeDeliveryThreshold,
            subMark: settings.subMark,
            largestPackVariants: this._largestPacks,
            starterPackProducts: settings.starterPackProducts,
            largestPackShipsFree: settings.largestPackShipsFree,
          })
        : null;

      const threshold = result ? result.threshold : 0;

      this.shippingProgress = {
        current: result ? result.eligible : 0,
        threshold,
        remaining: result ? result.remaining : threshold,
        met: result ? result.qualifies : false,
        percent: result ? result.percent : 0,
        reason: result ? result.reason : 'threshold',
      };

      // Legacy cart:updated field; real gift logic is gpFreeGift.decide().
      const giftThreshold = settings.giftThreshold == null ? threshold : Number(settings.giftThreshold);
      this.freeGiftEligible =
        items.length > 0 && this.shippingProgress.current >= giftThreshold && this.giftLines.length === 0;
    }

    // Read from inside #GpCartDrawer so it refreshes on each section swap.
    _learnLargestPacks() {
      const source = document.querySelector('[data-gp-largest-packs]');
      if (!source) return;

      const list = source.getAttribute('data-gp-largest-packs') || '';

      list
        .split(',')
        .filter(Boolean)
        .forEach((id) => {
          if (this._largestPacks.indexOf(`,${id},`) === -1) {
            this._largestPacks = `${this._largestPacks || ','}${id},`;
          }
        });
    }

    isGiftLine(item) {
      return isGiftLine(item);
    }

    get giftLines() {
      return ((this.cart && this.cart.items) || []).filter(isGiftLine);
    }

    _listenForForeignWrites() {
      const attach = () => {
        const events = (window.FGSECOMAPP && window.FGSECOMAPP.CUSTOM_EVENTS) || {};
        const names = [events.GIFT_UPDATED, events.BOGOS_FETCH_CART].filter(Boolean);

        if (!names.length) return false;

        names.forEach((name) => {
          document.addEventListener(name, () => this.refresh());
        });
        return true;
      };

      if (attach()) return;

      let attempts = 0;
      const timer = setInterval(() => {
        if (attach() || ++attempts > 20) clearInterval(timer);
      }, 500);
    }
  }

  window.cartState = new CartState();
})();
